// Slice 48 (V2.1) — invite-claim landing.
//
// Lives OUTSIDE the Shell. The invitee opens
// `https://hq.example.com/#/claim/<token>`, App.tsx detects the hash and
// routes here before the session branching. We:
//   1. GET /admin/invites/:token → render metadata (username, role,
//      invitedBy). 410 = expired/revoked/claimed → show a clean error.
//   2. Click "Register passkey" → POST /admin/invites/:token/begin →
//      navigator.credentials.create() via @simplewebauthn/browser → POST
//      /admin/invites/:token/finish.
//   3. On success the server mints a session cookie + returns
//      `{username, role}`; we call `onClaimed()` so App.tsx re-fetches
//      /admin/session and lands the user on Overview.

import { startRegistration } from "@simplewebauthn/browser";
import { useEffect, useState } from "react";
import { InvitesApi, type StaffInvitePreview, type StaffRole } from "../api/client";
import { HqBadge, HqButton, HqCard, HqInput, Icons, Logo } from "../ui";

interface ClaimScreenProps {
  token: string;
  onClaimed: () => void;
  onCancel: () => void;
}

type PreviewState =
  | { kind: "loading" }
  | { kind: "loaded"; preview: StaffInvitePreview }
  | { kind: "unusable"; message: string }
  | { kind: "registering"; preview: StaffInvitePreview }
  | { kind: "registered"; username: string; role: StaffRole };

export function ClaimScreen({ token, onClaimed, onCancel }: ClaimScreenProps) {
  const [state, setState] = useState<PreviewState>({ kind: "loading" });
  const [deviceName, setDeviceName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await InvitesApi.preview(token);
      if (cancelled) return;
      if (!res.ok) {
        setState({ kind: "unusable", message: res.error.message });
        return;
      }
      setState({ kind: "loaded", preview: res.data });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const register = async () => {
    if (state.kind !== "loaded") return;
    setState({ kind: "registering", preview: state.preview });
    setError(null);
    const beginRes = await InvitesApi.begin(token);
    if (!beginRes.ok) {
      setError(beginRes.error.message);
      setState({ kind: "loaded", preview: state.preview });
      return;
    }
    let att: unknown;
    try {
      att = await startRegistration({
        optionsJSON: beginRes.data as Parameters<typeof startRegistration>[0]["optionsJSON"],
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Passkey registration cancelled.");
      setState({ kind: "loaded", preview: state.preview });
      return;
    }
    const finishRes = await InvitesApi.finish(token, att, deviceName.trim() || undefined);
    if (!finishRes.ok) {
      setError(finishRes.error.message);
      setState({ kind: "loaded", preview: state.preview });
      return;
    }
    setState({ kind: "registered", username: finishRes.data.username, role: finishRes.data.role });
    // Tiny delay so the success state is visible before App.tsx swaps in
    // the Shell with the freshly authenticated session.
    setTimeout(onClaimed, 800);
  };

  return (
    <div
      style={{
        height: "100%",
        display: "grid",
        placeItems: "center",
        background: "var(--hq-bg)",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 18,
        }}
      >
        <Logo size={36} />
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            color: "var(--hq-text)",
            letterSpacing: "-.02em",
          }}
        >
          Join Vyvern HQ
        </h1>

        <HqCard>
          {state.kind === "loading" && (
            <div
              style={{
                padding: 12,
                fontSize: 12.5,
                color: "var(--hq-text-dim)",
                textAlign: "center",
              }}
            >
              Verifying invite…
            </div>
          )}

          {state.kind === "unusable" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 4 }}>
              <span style={{ fontSize: 13, color: "var(--hq-text)" }}>
                This invite link is no longer usable.
              </span>
              <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>{state.message}</span>
              <HqButton variant="secondary" onClick={onCancel}>
                Go to sign-in
              </HqButton>
            </div>
          )}

          {(state.kind === "loaded" || state.kind === "registering") && (
            <ClaimForm
              preview={state.kind === "loaded" ? state.preview : state.preview}
              deviceName={deviceName}
              setDeviceName={setDeviceName}
              onRegister={() => void register()}
              registering={state.kind === "registering"}
              error={error}
            />
          )}

          {state.kind === "registered" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}
            >
              <Icons.Check width={22} height={22} sw={2.2} style={{ color: "var(--hq-success)" }} />
              <span style={{ fontSize: 14, color: "var(--hq-text)" }}>
                Welcome, <strong>{state.username}</strong>.
              </span>
              <HqBadge tone={state.role === "admin" ? "accent" : "neutral"} size="sm">
                {state.role}
              </HqBadge>
              <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
                Loading your back-office…
              </span>
            </div>
          )}
        </HqCard>
      </div>
    </div>
  );
}

function ClaimForm({
  preview,
  deviceName,
  setDeviceName,
  onRegister,
  registering,
  error,
}: {
  preview: StaffInvitePreview;
  deviceName: string;
  setDeviceName: (v: string) => void;
  onRegister: () => void;
  registering: boolean;
  error: string | null;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>Invited as</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="hq-mono" style={{ fontSize: 14, color: "var(--hq-text)" }}>
            {preview.username}
          </span>
          <HqBadge tone={preview.role === "admin" ? "accent" : "neutral"} size="sm">
            {preview.role}
          </HqBadge>
        </div>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-dim)" }}>
          Invited by <span className="hq-mono">{preview.invitedBy}</span>.
        </span>
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: "var(--hq-text-muted)", lineHeight: 1.5 }}>
        Vyvern HQ uses <strong style={{ color: "var(--hq-text)" }}>passkeys</strong> instead of
        passwords. Your browser will create a credential tied to this device (or your phone, or a
        security key) and sign you in. You can add more devices afterwards from the Staff screen.
      </p>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, color: "var(--hq-text-dim)" }}>
          Name this device (optional)
        </span>
        <HqInput
          placeholder={"MacBook · home office"}
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
        />
      </label>

      {error && (
        <div
          style={{
            padding: "8px 10px",
            background: "rgba(251,113,133,.08)",
            border: "1px solid rgba(251,113,133,.3)",
            borderRadius: "var(--hq-radius-md)",
            color: "var(--hq-danger)",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      <HqButton onClick={onRegister} disabled={registering}>
        {registering ? "Registering passkey…" : "Register passkey & sign in"}
      </HqButton>
    </div>
  );
}
